import { Component, OnInit } from '@angular/core';
import { NavBreadcrumb, PageRequest, PropsAction } from '@models/models';
import {
	QuestionnaireTemplate,
	QuestionnaireTemplatesPreviews,
	ResponseActions
} from '@web-components/settings/settings.model';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { SNModalService } from '@web-services/modal/snModal.service';
import { DeleteItemModalComponent } from '@web-components/settings/common/delete-item-modal/delete-item-modal';
import { finalize, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs/Subject';
import { MessageService } from '@services/helpers/message-service/message.service';
import { NewSubscriptionServiceDataProvider } from '@services/subscription/subscription.service';
import { SettingsDataService } from '@web-services/settings/contact-form/settings.data.service';
import { AccountServiceWebProvider } from '@web-services/user/account.service';

@Component({
	selector: 'questionnaire-section',
	template: require('./questionnaire-section.html'),
	styles: [require('./questionnaire-section.scss')]
})
export class QuestionnaireSectionComponent implements OnInit {
	breadcrumbs: Array<NavBreadcrumb> = [
		{ link: '/', title: 'Dashboard' },
		{ link: '/settings', title: 'Settings' },
		{ link: null, title: 'Questionnaire Templates' }
	];
	questionnaireTemplatesPreviews: QuestionnaireTemplatesPreviews;
	btnDisable: boolean;
	pageRequest: PageRequest;
	companyId: number;

	private deleteModalRef: NgbModalRef;
	private unsubscribe: Subject<void> = new Subject();

	private readonly REQUEST_PARAMS: PageRequest = {
		paging: {
			pageNumber: 0,
			pageSize: 5,
			searchToken: null
		},
		sortCriterias: [
			{
				fieldName: 'name',
				sortAscending: true
			}
		]
	};

	constructor(
		private snModalService: SNModalService,
		private settingsDataService: SettingsDataService,
		private accountService: AccountServiceWebProvider,
		private subscriptionService: NewSubscriptionServiceDataProvider,
		private messageService: MessageService
	) {}

	ngOnInit(): void {
		this.getCompanyId();
		this.applyPageProductFromLocalStorage();
		this.getQuestionnaireTemplatesPreviews();
	}

	addNewTemplate(): void {
		const {
			url
		} = this.questionnaireTemplatesPreviews.actions.CREATE_NEW_TEMPLATE;
		this.settingsDataService
			.getNewTemplateURL(url)
			.pipe(takeUntil(this.unsubscribe))
			.subscribe(
				(data: ResponseActions): void =>
					this.openQuestionnaireBuilder(data.actions.OPEN_IN_EDITOR.url),
				(err): void => this.handleError(err)
			);
	}

	editTemplate(template: QuestionnaireTemplate): void {
		window.open(`${template.actions.OPEN_IN_EDITOR.url}`, '_self');
	}

	duplicateTemplate(template: QuestionnaireTemplate): void {
		if (!this.isValidTemplate(template)) {
			return;
		}

		this.btnDisable = true;
		this.settingsDataService
			.duplicateQuestionnaireTemplate(
				template.actions.DUPLICATE_TEMPLATE.url,
				`${template.name} copy`
			)
			.pipe(
				takeUntil(this.unsubscribe),
				finalize((): boolean => (this.btnDisable = false))
			)
			.subscribe(
				(data: ResponseActions): void => {
					this.openQuestionnaireBuilder(
						data.actions.OPEN_IN_EDITOR.url,
						'state=duplicate'
					);
				},
				(err): void => this.handleError(err)
			);
	}

	deleteTemplate(template: QuestionnaireTemplate): void {
		this.deleteModalRef = this.snModalService.openModal(
			DeleteItemModalComponent
		);

		this.deleteModalRef.componentInstance.btnDisabled = false;
		this.deleteModalRef.componentInstance.itemName = template.name;
		this.deleteModalRef.componentInstance.modalTitle = 'questionnaire';
		this.deleteModalRef.componentInstance.modalData = template;

		const modalData$ = this.snModalService
			.getModalData()
			.subscribe((data: PropsAction<QuestionnaireTemplate>): void => {
				this.deleteModalRef.componentInstance.btnDisabled = true;
				this.settingsDataService
					.deleteQuestionnaireTemplate(data.props.actions.DELETE_TEMPLATE.url)
					.pipe(
						takeUntil(this.unsubscribe),
						finalize(
							(): boolean =>
								(this.deleteModalRef.componentInstance.btnDisabled = false)
						)
					)
					.subscribe((): void => {
						this.pageRequest.paging.pageNumber = 0;
						this.pageRequest.paging.searchToken = null;
						this.getQuestionnaireTemplatesPreviews();
						this.messageService.showToaster(
							's_0',
							'questionnaires',
							template.name
						);
						this.deleteModalRef.close();
						modalData$.unsubscribe();
					});
			});
	}

	updatePageRequestParams(data: PageRequest): void {
		this.pageRequest = data;
		this.getQuestionnaireTemplatesPreviews();
	}

	private getQuestionnaireTemplatesPreviews(): void {
		this.settingsDataService
			.getQuestionnaireTemplatesPreviews(this.pageRequest)
			.pipe(takeUntil(this.unsubscribe))
			.subscribe((data: QuestionnaireTemplatesPreviews): void => {
				this.questionnaireTemplatesPreviews = data;
				this.pageRequest.paging.searchToken = data.templatePreviews.searchToken;
				this.setPageProductToLocalStorage(this.pageRequest);
			});
	}

	private applyPageProductFromLocalStorage(): void {
		const pageRequestObject = JSON.parse(
			localStorage.getItem(`questionnaireTemplatesRequest_${this.companyId}`)
		);

		this.pageRequest = this.REQUEST_PARAMS;

		if (pageRequestObject && pageRequestObject !== '') {
			const { pageSize, fieldName, sortAscending } = pageRequestObject;
			this.pageRequest.paging.pageSize = pageSize;
			this.pageRequest.sortCriterias[0].fieldName = fieldName;
			this.pageRequest.sortCriterias[0].sortAscending = sortAscending;
		}
	}

	private openQuestionnaireBuilder(url: string, param?: string): void {
		let urlString = url;
		if (param) {
			urlString += `?${param}`;
		}
		window.open(`${urlString}`, '_self');
	}

	private setPageProductToLocalStorage(pageRequest: PageRequest): void {
		const pageRequestObject = {
			pageSize: pageRequest.paging.pageSize,
			fieldName: pageRequest.sortCriterias[0].fieldName,
			sortAscending: pageRequest.sortCriterias[0].sortAscending
		};

		const pageRequestString = JSON.stringify(pageRequestObject);

		localStorage.removeItem(`questionnaireTemplatesRequest_${this.companyId}`);
		localStorage.setItem(
			`questionnaireTemplatesRequest_${this.companyId}`,
			pageRequestString
		);
	}

	private getCompanyId(): void {
		this.accountService.companyIdObservable
			.pipe(takeUntil(this.unsubscribe))
			.subscribe((data: number): number => (this.companyId = data));
	}

	private isValidTemplate(template: QuestionnaireTemplate): boolean {
		if (template.name.length > 250) {
			this.messageService.showToaster('e_1', 'questionnaires');
			return false;
		}

		return true;
	}

	private handleError({ error }): void {
		const { errorTitle, errorMessage } = error;
		this.messageService.showErrorFromBackend(errorTitle, errorMessage);
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=oom9nsliule!./web/modern/components/settings/questionnaire-section/questionnaire-section.ts